// DEV ONLY — dummy run output for testing the side panel without the backend.
// Turn on in the browser console: localStorage.setItem('mockRunOutput', '1'), then reload.
// Turn off: localStorage.removeItem('mockRunOutput').
// Each run id picks a fixture by `runId % fixtures.length`, so different rows show different cases.
import type {
  FileResultDTO,
} from 'dtos/fileResultDTO';
import type {
  ReportOutputDTO,
} from 'dtos/reportOutputDTO';
import type {
  ReportOutputFilePreviewDTO,
} from 'dtos/reportOutputFilePreviewDTO';
import type {
  RunOutputSource,
} from 'views/EMR/components/Panel/RunOutputPanel/types';

const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const reportFixtures: ReportOutputDTO[] = [
  // Ready, several files, with exceptions
  {
    message: 'The report finished with 20 exceptions. See the exceptions file for details.',
    summary: [
      { label: 'Claims included', value: '1,248', tone: 'Default' },
      { label: 'Total billed', value: '$184,920.50', tone: 'Default' },
      { label: 'Exceptions', value: '20', tone: 'Warning' },
      { label: 'Denied claims', value: '3', tone: 'Danger' },
    ],
    settings: {
      'Date range': '09/01/2026 – 09/30/2026',
      Facility: 'Main Street Clinic',
      Provider: 'All providers',
      'Payer type': 'Commercial, Medicare',
    },
    files: [
      { reportOutputFileId: 101, fileName: 'claims-summary-sep-2026.xlsx', contentType: XLSX, sizeBytes: 482_113, itemCount: 1248, canPreview: false },
      { reportOutputFileId: 102, fileName: 'claims-exceptions-sep-2026.csv', contentType: 'text/csv', sizeBytes: 6_204, itemCount: 20, canPreview: true },
      { reportOutputFileId: 103, fileName: 'claims-summary-sep-2026.pdf', contentType: 'application/pdf', sizeBytes: 1_920_440, itemCount: null, canPreview: true },
    ],
  },
  // Ready, one file, no exceptions
  {
    message: null,
    summary: [
      { label: 'Patients included', value: '312', tone: 'Default' },
      { label: 'Exceptions', value: '0', tone: 'Default' },
    ],
    settings: {
      'Date range': '10/01/2026 – 10/25/2026',
      Facility: 'All facilities',
    },
    files: [
      { reportOutputFileId: 201, fileName: 'patient-balance-oct-2026.csv', contentType: 'text/csv', sizeBytes: 38_512, itemCount: 312, canPreview: true },
    ],
  },
  // Failed
  {
    message: 'The report could not be generated: the query timed out after 10 minutes. Try a shorter date range.  Try a shorter date range. Try a shorter date range.',
    summary: [
      { label: 'Rows processed', value: '58,000 of ~120,000', tone: 'Danger' },
    ],
    settings: {
      'Date range': '01/01/2025 – 10/25/2026',
      Facility: 'All facilities',
    },
    files: [],
  },
  // Old run: one file with a null id, no summary, no settings
  {
    message: null,
    summary: [],
    settings: {},
    files: [
      { reportOutputFileId: null, fileName: 'legacy-report.xlsx', contentType: XLSX, sizeBytes: null, itemCount: null, canPreview: false },
    ],
  },
];

const statementFixtures: ReportOutputDTO[] = [
  // Ready, several files, with exceptions
  {
    message: '28 patients were skipped because they have no mailing address.',
    summary: [
      { label: 'Statements generated', value: '1,412', tone: 'Default' },
      { label: 'Total balance due', value: '$96,340.12', tone: 'Default' },
      { label: 'Skipped patients', value: '28', tone: 'Warning' },
    ],
    settings: {
      'Statement date': '10/20/2026',
      'Minimum balance': '$5.00',
      'Include zero-balance': 'No',
      Facility: 'Main Street Clinic',
    },
    files: [
      { reportOutputFileId: 301, fileName: 'statements-batch-1-of-2.pdf', contentType: 'application/pdf', sizeBytes: 8_402_118, itemCount: 750, canPreview: true },
      { reportOutputFileId: 302, fileName: 'statements-batch-2-of-2.pdf', contentType: 'application/pdf', sizeBytes: 7_120_904, itemCount: 662, canPreview: true },
      { reportOutputFileId: 303, fileName: 'statement-exceptions.csv', contentType: 'text/csv', sizeBytes: 2_310, itemCount: 28, canPreview: true },
    ],
  },
  // Ready, one file
  {
    message: null,
    summary: [
      { label: 'Statements generated', value: '86', tone: 'Default' },
      { label: 'Total balance due', value: '$4,118.00', tone: 'Default' },
    ],
    settings: {
      'Statement date': '10/24/2026',
      'Minimum balance': '$0.00',
    },
    files: [
      { reportOutputFileId: 401, fileName: 'statements.pdf', contentType: 'application/pdf', sizeBytes: 512_300, itemCount: 86, canPreview: true },
    ],
  },
  // Failed
  {
    message: 'Statement generation failed: the statement template is missing for this practice.',
    summary: [],
    settings: {
      'Statement date': '10/22/2026',
    },
    files: [],
  },
];

export function isRunOutputMockEnabled(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    return localStorage.getItem('mockRunOutput') === '1';
  } catch {
    return false;
  }
}

const delay = <T>(value: T, ms = 600) => new Promise<T>((resolve) => {
  setTimeout(() => resolve(value), ms);
});

function getFixture(source: RunOutputSource): ReportOutputDTO {
  const fixtures = source.kind === 'report' ? reportFixtures : statementFixtures;
  const runId = source.kind === 'report' ? source.reportId : source.bulkPatientStatementId;
  return fixtures[Math.abs(runId) % fixtures.length];
}

function getFile(source: RunOutputSource, reportOutputFileId: number | null | undefined) {
  const files = getFixture(source).files ?? [];
  return files.find((file) => file.reportOutputFileId === (reportOutputFileId ?? null)) ?? files[0];
}

function getMockText(fileName: string) {
  return `Mock file: ${fileName}\nGenerated for side panel testing.\n`;
}

export const mockRunEndpoints = (source: RunOutputSource) => ({
  getOutput: () => delay(getFixture(source)),
  downloadFile: (reportOutputFileId: number | null | undefined): Promise<FileResultDTO> => {
    const fileName = getFile(source, reportOutputFileId)?.fileName ?? 'mock.txt';
    // Contents are plain text whatever the extension, so the downloaded file only opens as text.
    return delay({ fileName, contentType: 'text/plain', contents: btoa(getMockText(fileName)) });
  },
  getPreviewUrl: (reportOutputFileId: number | null | undefined): Promise<ReportOutputFilePreviewDTO> => {
    const fileName = getFile(source, reportOutputFileId)?.fileName ?? 'mock.txt';
    const url = URL.createObjectURL(new Blob([getMockText(fileName)], { type: 'text/plain' }));
    return delay({ url, expiresOn: new Date(Date.now() + 10 * 60_000).toISOString() });
  },
});
